// ============================================
// DashboardPage — UKA-X01 D8 (revised)
//
// OPERATOR-only team dashboard. REVIEWER role redirected to /portal/review.
// Real-time Firestore listener via useReviewContent hook.
//
// Filters (stacked AND logic): category × status × tier
//   - category: item.category (string, 8 groupings)
//   - status:   item.status (ContentStatus enum, 6 values)
//   - tier:     item.tier (supplement | rx | longevity | standard | null)
//
// Each row: title, category badge, status chip, reviewer display name,
//   last verdict timestamp, C10 propagation status (SYNCED / FAILED / PENDING).
//
// Summary counts reflect filtered list when any filter is active.
//
// Design: DM Sans body, Instrument Serif title only, no framer-motion.
// Palette: BG #FAFAFA, Cards #FFFFFF, Text #18181B, Rose #E11D48.
// Min 13px font, WCAG AA contrast, 4px border-radius.
// ============================================

import React, { useMemo, useState } from 'react';
import type { Timestamp } from 'firebase/firestore';
import { PortalAuthGuard } from '../lib/authGuard';
import { useReviewContent } from '../hooks/useReviewContent';
import type { ClinicalReviewItemWithId } from '../hooks/useReviewContent';
import { DashboardSummary } from '../components/DashboardSummary';
import type { ContentStatus } from '../../data/portal/schema';

// ── Category options (8 spec-defined groupings) ────────────────────────────────

const CATEGORY_OPTIONS: { label: string; value: string | 'ALL' }[] = [
  { label: 'All Categories', value: 'ALL' },
  { label: 'Methodology', value: 'Methodology' },
  { label: 'Biomarker Ranges', value: 'Biomarker Ranges' },
  { label: 'Formulary Supplement', value: 'Formulary Supplement' },
  { label: 'Formulary Rx', value: 'Formulary Rx' },
  { label: 'Formulary Longevity', value: 'Formulary Longevity' },
  { label: 'Evidence Briefs', value: 'Evidence Briefs' },
  { label: 'Gap Questions', value: 'Gap Questions' },
  { label: 'Attestation', value: 'Attestation' },
];

// ── Status options (6 active review states) ────────────────────────────────────

const STATUS_OPTIONS: { label: string; value: ContentStatus | 'ALL' }[] = [
  { label: 'All Statuses', value: 'ALL' },
  { label: 'Staged', value: 'STAGED' },
  { label: 'In Review', value: 'IN_REVIEW' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Approved w/ Conditions', value: 'APPROVED_WITH_CONDITIONS' },
  { label: 'Change Requested', value: 'CHANGE_REQUESTED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'Withdrawn', value: 'WITHDRAWN' },
];

// ── Tier options ───────────────────────────────────────────────────────────────

const TIER_OPTIONS: { label: string; value: string | 'ALL' }[] = [
  { label: 'All Tiers', value: 'ALL' },
  { label: 'Supplement', value: 'supplement' },
  { label: 'Rx', value: 'rx' },
  { label: 'Longevity', value: 'longevity' },
  { label: 'Standard', value: 'standard' },
];

// ── "Reviewed" = any terminal state ───────────────────────────────────────────

const REVIEWED_STATUSES: ContentStatus[] = [
  'APPROVED',
  'APPROVED_WITH_CONDITIONS',
  'REJECTED',
  'CHANGE_REQUESTED',
  'WITHDRAWN',
];

// ── Color helpers ──────────────────────────────────────────────────────────────

function statusChipStyle(status: ContentStatus): React.CSSProperties {
  switch (status) {
    case 'APPROVED':
      return { backgroundColor: '#DCFCE7', color: '#15803D', borderColor: '#86EFAC' };
    case 'APPROVED_WITH_CONDITIONS':
      return { backgroundColor: '#F0FDF4', color: '#166534', borderColor: '#BBF7D0' };
    case 'REJECTED':
      return { backgroundColor: '#FEF2F2', color: '#B91C1C', borderColor: '#FCA5A5' };
    case 'CHANGE_REQUESTED':
      return { backgroundColor: '#FFFBEB', color: '#92400E', borderColor: '#FCD34D' };
    case 'WITHDRAWN':
      return { backgroundColor: '#F4F4F5', color: '#52525B', borderColor: '#D4D4D8' };
    case 'STAGED':
      return { backgroundColor: '#F5F3FF', color: '#6D28D9', borderColor: '#DDD6FE' };
    case 'IN_REVIEW':
    default:
      return { backgroundColor: '#EFF6FF', color: '#1D4ED8', borderColor: '#BFDBFE' };
  }
}

function propagationChipStyle(status: string | undefined): React.CSSProperties {
  switch (status) {
    case 'SYNCED':
      return { backgroundColor: '#DCFCE7', color: '#15803D', borderColor: '#86EFAC' };
    case 'FAILED':
      return { backgroundColor: '#FEF2F2', color: '#B91C1C', borderColor: '#FCA5A5' };
    case 'PENDING':
    default:
      return { backgroundColor: '#F4F4F5', color: '#71717A', borderColor: '#D4D4D8' };
  }
}

function chipBase(extra: React.CSSProperties): React.CSSProperties {
  return {
    display: 'inline-block',
    fontSize: '12px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    border: '1px solid',
    whiteSpace: 'nowrap',
    ...extra,
  };
}

// ── Timestamp formatter ────────────────────────────────────────────────────────

function formatTimestamp(ts: Timestamp | null | undefined): string {
  if (!ts) return '—';
  try {
    const d = ts.toDate();
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

// ── Status label helper ────────────────────────────────────────────────────────

function statusLabel(status: ContentStatus): string {
  const found = STATUS_OPTIONS.find((s) => s.value === status);
  return found ? found.label : status;
}

// ── Filter select ──────────────────────────────────────────────────────────────

interface FilterSelectProps<T extends string> {
  label: string;
  value: T;
  options: { label: string; value: T }[];
  onChange: (val: T) => void;
}

function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: FilterSelectProps<T>) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        fontSize: '13px',
        color: '#52525B',
        fontWeight: 500,
        fontFamily: 'DM Sans, system-ui, sans-serif',
      }}
    >
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        style={{
          fontSize: '14px',
          fontFamily: 'DM Sans, system-ui, sans-serif',
          color: '#18181B',
          backgroundColor: '#FFFFFF',
          border: '1px solid #D4D4D8',
          borderRadius: '4px',
          padding: '8px 10px',
          minHeight: '40px',
          cursor: 'pointer',
          minWidth: '160px',
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// ── Table row ──────────────────────────────────────────────────────────────────

interface RowProps {
  item: ClinicalReviewItemWithId;
}

function DashboardRow({ item }: RowProps) {
  // C10 propagation status — written by C10 Cloud Function on the Firestore doc.
  // Now typed as ClinicalReviewItem.propagationStatus (added to schema D8 revision).
  const propagation = item.propagationStatus;

  // Use lockedAt timestamp for approved sections, stagedAt as fallback.
  const lastTs = item.lockedAt ?? item.stagedAt;

  // Reviewer display name: use denormalized currentReviewerDisplayName if available,
  // fall back to currentReviewer UID as last resort.
  const reviewerDisplay = item.currentReviewerDisplayName ?? item.currentReviewer ?? '—';

  return (
    <tr style={{ borderBottom: '1px solid #F4F4F5' }}>
      {/* Title */}
      <td
        style={{
          padding: '14px 16px',
          fontSize: '14px',
          color: '#18181B',
          fontWeight: 500,
          maxWidth: '300px',
        }}
      >
        <span
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={item.title}
        >
          {item.title}
        </span>
      </td>

      {/* Category badge */}
      <td style={{ padding: '14px 12px', whiteSpace: 'nowrap' }}>
        <span style={chipBase({ backgroundColor: '#F4F4F5', color: '#3F3F46', borderColor: '#D4D4D8' })}>
          {item.category ?? '—'}
        </span>
      </td>

      {/* Tier badge */}
      <td style={{ padding: '14px 12px', whiteSpace: 'nowrap' }}>
        {item.tier ? (
          <span style={chipBase({ backgroundColor: '#F0F9FF', color: '#0369A1', borderColor: '#BAE6FD' })}>
            {item.tier}
          </span>
        ) : (
          <span style={{ fontSize: '13px', color: '#A1A1AA' }}>—</span>
        )}
      </td>

      {/* Status chip */}
      <td style={{ padding: '14px 12px', whiteSpace: 'nowrap' }}>
        <span style={chipBase(statusChipStyle(item.status))}>
          {statusLabel(item.status)}
        </span>
      </td>

      {/* Reviewer name */}
      <td
        style={{
          padding: '14px 12px',
          fontSize: '13px',
          color: item.currentReviewer ? '#18181B' : '#A1A1AA',
          whiteSpace: 'nowrap',
          maxWidth: '200px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={reviewerDisplay !== '—' ? reviewerDisplay : undefined}
      >
        {reviewerDisplay}
      </td>

      {/* Last verdict timestamp */}
      <td
        style={{
          padding: '14px 12px',
          fontSize: '13px',
          color: '#52525B',
          whiteSpace: 'nowrap',
        }}
      >
        {formatTimestamp(lastTs)}
      </td>

      {/* C10 sync status */}
      <td style={{ padding: '14px 12px', whiteSpace: 'nowrap' }}>
        <span style={chipBase(propagationChipStyle(propagation))}>
          {propagation ?? 'PENDING'}
        </span>
      </td>
    </tr>
  );
}

// ── DashboardPageInner ─────────────────────────────────────────────────────────

function DashboardPageInner() {
  const { sections, loading, error } = useReviewContent();

  // Three independent filters — stacked with AND logic
  const [categoryFilter, setCategoryFilter] = useState<string | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<ContentStatus | 'ALL'>('ALL');
  const [tierFilter, setTierFilter] = useState<string | 'ALL'>('ALL');

  // ── Filtered list ────────────────────────────────────────────────────────

  const filteredSections = useMemo(() => {
    return sections.filter((s) => {
      const categoryMatch = categoryFilter === 'ALL' || s.category === categoryFilter;
      const statusMatch = statusFilter === 'ALL' || s.status === statusFilter;
      // tier null matches 'ALL' only
      const tierMatch =
        tierFilter === 'ALL' || (tierFilter === '' ? s.tier == null : s.tier === tierFilter);
      return categoryMatch && statusMatch && tierMatch;
    });
  }, [sections, categoryFilter, statusFilter, tierFilter]);

  const isFiltered = categoryFilter !== 'ALL' || statusFilter !== 'ALL' || tierFilter !== 'ALL';

  // ── Summary counts (reflect filtered list when filter is active) ──────────

  const summarySource = isFiltered ? filteredSections : sections;

  const summaryTotal = summarySource.length;
  const summaryReviewed = summarySource.filter((s) =>
    REVIEWED_STATUSES.includes(s.status),
  ).length;
  const summaryApproved = summarySource.filter(
    (s) => s.status === 'APPROVED' || s.status === 'APPROVED_WITH_CONDITIONS',
  ).length;
  const summaryRejected = summarySource.filter((s) => s.status === 'REJECTED').length;
  const summaryChangeRequested = summarySource.filter(
    (s) => s.status === 'CHANGE_REQUESTED',
  ).length;
  const summaryPending = summarySource.filter(
    (s) => s.status === 'IN_REVIEW' || s.status === 'STAGED',
  ).length;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#FAFAFA',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <header
        style={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E4E4E7',
          padding: '0 20px',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 40,
        }}
      >
        <h1
          style={{
            fontFamily: 'Instrument Serif, Georgia, serif',
            fontSize: '20px',
            fontWeight: 400,
            color: '#18181B',
            margin: 0,
          }}
        >
          Team Dashboard
        </h1>
        <span style={{ fontSize: '13px', color: '#A1A1AA' }}>
          {sections.length} section{sections.length !== 1 ? 's' : ''} total
        </span>
      </header>

      {/* Main content */}
      <main
        style={{
          flex: 1,
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          maxWidth: '1400px',
          width: '100%',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Loading state */}
        {loading && (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E4E4E7',
              borderRadius: '4px',
              padding: '24px 20px',
              fontSize: '16px',
              color: '#A1A1AA',
              textAlign: 'center',
            }}
          >
            Loading dashboard data…
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div
            style={{
              backgroundColor: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: '4px',
              padding: '14px 16px',
              fontSize: '16px',
              color: '#B91C1C',
            }}
            role="alert"
          >
            Failed to load content: {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Summary stats */}
            <DashboardSummary
              total={summaryTotal}
              reviewed={summaryReviewed}
              approved={summaryApproved}
              rejected={summaryRejected}
              changeRequested={summaryChangeRequested}
              pending={summaryPending}
              isFiltered={isFiltered}
            />

            {/* Filters — category × status × tier */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E4E4E7',
                borderRadius: '4px',
                padding: '16px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                alignItems: 'flex-end',
              }}
            >
              <FilterSelect<string | 'ALL'>
                label="Category"
                value={categoryFilter}
                options={CATEGORY_OPTIONS}
                onChange={setCategoryFilter}
              />
              <FilterSelect<ContentStatus | 'ALL'>
                label="Status"
                value={statusFilter}
                options={STATUS_OPTIONS}
                onChange={setStatusFilter}
              />
              <FilterSelect<string | 'ALL'>
                label="Tier"
                value={tierFilter}
                options={TIER_OPTIONS}
                onChange={setTierFilter}
              />
              {isFiltered && (
                <button
                  onClick={() => {
                    setCategoryFilter('ALL');
                    setStatusFilter('ALL');
                    setTierFilter('ALL');
                  }}
                  style={{
                    fontSize: '14px',
                    fontFamily: 'DM Sans, system-ui, sans-serif',
                    color: '#E11D48',
                    backgroundColor: 'transparent',
                    border: '1px solid #E11D48',
                    borderRadius: '4px',
                    padding: '8px 14px',
                    cursor: 'pointer',
                    minHeight: '40px',
                    alignSelf: 'flex-end',
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>

            {/* Section table */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E4E4E7',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              {/* Result count */}
              <div
                style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid #F4F4F5',
                  fontSize: '13px',
                  color: '#71717A',
                  fontWeight: 500,
                }}
              >
                {filteredSections.length} section
                {filteredSections.length !== 1 ? 's' : ''}
                {isFiltered ? ' (filtered)' : ''}
              </div>

              {filteredSections.length === 0 ? (
                <div
                  style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    fontSize: '16px',
                    color: '#A1A1AA',
                  }}
                >
                  No sections match the current filters.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      minWidth: '860px',
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: '2px solid #E4E4E7',
                          backgroundColor: '#FAFAFA',
                        }}
                      >
                        {(['Section Title', 'Category', 'Tier', 'Status', 'Reviewer', 'Last Verdict', 'C10 Sync'] as const).map(
                          (col) => (
                            <th
                              key={col}
                              style={{
                                padding: '10px 12px 10px 16px',
                                fontSize: '12px',
                                fontWeight: 700,
                                color: '#52525B',
                                textAlign: 'left',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {col}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSections.map((item) => (
                        <DashboardRow key={item.id} item={item} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// ── DashboardPage (auth-guarded export) ────────────────────────────────────────
// OPERATOR role required.
// REVIEWER (role rank < OPERATOR) is redirected to /portal/review — their workspace.
// ADMIN passes (role rank > OPERATOR).

export function DashboardPage() {
  return (
    <PortalAuthGuard requiredRole="OPERATOR" unauthorizedRedirectTo="/portal/review">
      <DashboardPageInner />
    </PortalAuthGuard>
  );
}
